import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Palette, 
  ShoppingBag, 
  Camera, 
  Building2, 
  Users, 
  Gamepad2, 
  PenTool, 
  Share2,
  ArrowRight 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Import high-quality use case images as fallbacks
import usecaseMarketing from "@/assets/usecase-marketing.jpg";
import usecaseEcommerce from "@/assets/usecase-ecommerce.jpg";
import usecasePhotography from "@/assets/usecase-photography.jpg";
import usecaseRealEstate from "@/assets/usecase-real-estate.jpg";
import usecaseSocialMedia from "@/assets/usecase-social-media.jpg";
import usecaseGaming from "@/assets/usecase-gaming.jpg";
import usecaseCreativeAgencies from "@/assets/usecase-creative-agencies.jpg";
import usecasePublishing from "@/assets/usecase-publishing.jpg";

const UseCases = () => {
  const navigate = useNavigate();
  const [industryImages, setIndustryImages] = useState<{[key: string]: string}>({});
  
  // Fallback images mapping
  const fallbackImages = {
    'marketing': usecaseMarketing,
    'ecommerce': usecaseEcommerce,
    'photography': usecasePhotography,
    'real-estate': usecaseRealEstate,
    'social-media': usecaseSocialMedia,
    'gaming': usecaseGaming,
    'creative-agencies': usecaseCreativeAgencies,
    'publishing': usecasePublishing
  };

  useEffect(() => {
    fetchIndustryImages();
  }, []);

  const fetchIndustryImages = async () => {
    try {
      const { data, error } = await supabase
        .from('industry_images')
        .select('industry, image_url')
        .eq('display_order', 1); // Get the primary image for each industry

      if (error) throw error;

      const imageMap: {[key: string]: string} = {};
      data?.forEach(item => {
        imageMap[item.industry] = item.image_url;
      });
      
      setIndustryImages(imageMap);
    } catch (error) {
      console.error('Error fetching industry images:', error);
      // Will use fallback images
    }
  };

  const getIndustryImage = (industryKey: string) => {
    return industryImages[industryKey] || fallbackImages[industryKey as keyof typeof fallbackImages];
  };
  
  const industries = [
    {
      title: "Marketing",
      icon: Share2,
      description: "Create compelling ad visuals, social media content, and campaign materials at scale.",
      gradient: "from-blue-500 to-cyan-500",
      route: "/industries/marketing",
      key: "marketing"
    },
    {
      title: "E-commerce",
      icon: ShoppingBag,
      description: "Generate product images, lifestyle shots, and promotional graphics for online stores.",
      gradient: "from-purple-500 to-pink-500",
      route: "/industries/ecommerce",
      key: "ecommerce"
    },
    {
      title: "Photography",
      icon: Camera,
      description: "Enhance workflows with AI-generated backgrounds, concepts, and artistic elements.",
      gradient: "from-green-500 to-teal-500",
      route: "/industries/photography",
      key: "photography"
    },
    {
      title: "Real Estate",
      icon: Building2,
      description: "Create virtual staging, property visualizations, and marketing materials.",
      gradient: "from-orange-500 to-red-500",
      route: "/industries/real-estate",
      key: "real-estate"
    },
    {
      title: "Social Media",
      icon: Users,
      description: "Generate engaging posts, stories, and content for various platforms.",
      gradient: "from-indigo-500 to-purple-500",
      route: "/industries/social-media",
      key: "social-media"
    },
    {
      title: "Gaming",
      icon: Gamepad2,
      description: "Create game assets, concept art, and promotional materials for gaming projects.",
      gradient: "from-pink-500 to-rose-500",
      route: "/industries/gaming",
      key: "gaming"
    },
    {
      title: "Creative Agencies",
      icon: PenTool,
      description: "Streamline creative workflows with AI-powered visual content generation.",
      gradient: "from-cyan-500 to-blue-500",
      route: "/industries/creative-agencies",
      key: "creative-agencies"
    },
    {
      title: "Publishing",
      icon: Palette,
      description: "Generate book covers, illustrations, and editorial graphics efficiently.",
      gradient: "from-emerald-500 to-green-500",
      route: "/industries/publishing",
      key: "publishing"
    }
  ];

  const handleIndustryClick = (route: string) => {
    // Navigate to the top of the target page
    navigate(route, { state: { scrollTo: null } });
    // Scroll to top immediately
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="use-cases" className="py-12 sm:py-16 lg:py-20 bg-slate-50 relative overflow-hidden">
      {/* Grid Background Pattern - Similar to Hero Section */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
            Powering Innovation
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent block mt-2">
              Across Industries
            </span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-xs sm:max-w-2xl lg:max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
            From startups to enterprises, see how businesses leverage our AI to transform their visual content creation.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {industries.map((industry, index) => (
            <Card key={industry.title} className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0 h-full flex flex-col">
                {/* High-quality image with overlay */}
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img 
                    src={getIndustryImage(industry.key)} 
                    alt={`${industry.title} use case`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const fallbackSrc = fallbackImages[industry.key as keyof typeof fallbackImages];
                      target.src = fallbackSrc || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className={`absolute top-4 right-4 w-10 h-10 bg-gradient-to-r ${industry.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <industry.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                
                {/* Content section */}
                <div className="p-6 sm:p-8 flex flex-col flex-grow">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">{industry.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-xs sm:text-sm flex-grow">{industry.description}</p>
                  <Button 
                    onClick={() => handleIndustryClick(industry.route)}
                    className={`w-full bg-gradient-to-r ${industry.gradient} hover:shadow-lg transition-all duration-300 text-white border-0 group-hover:scale-105`}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
